import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Database, ExternalLink, RefreshCw, ShieldCheck, TriangleAlert, Users, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/Layout";
import { EmptyState, ErrorState, LoadingGrid, LoginPrompt } from "@/components/common/States";
import { KINDS } from "@/lib/kinds";
import { formatNumber } from "@/lib/format";

const dateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function MappingStatus({ mapping }) {
  if (!mapping) return <span className="text-xs text-muted-foreground">—</span>;
  if (!mapping.ok) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><XCircle className="h-3.5 w-3.5" /> {mapping.error}</span>;
  if (mapping.missing.length) {
    return (
      <span className="inline-flex items-start gap-1 text-xs font-semibold text-amber-700">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Propriétés introuvables : {mapping.missing.join(", ")}
      </span>
    );
  }
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Mapping OK</span>;
}

export default function AdminPage() {
  const { isAdmin, isAuthed, loading } = useAuth();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["admin-status"],
    queryFn: async () => (await api.get("/admin/status")).data,
    enabled: isAdmin,
    refetchInterval: (query) => (query.state.data?.syncing ? 2000 : false),
  });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data, enabled: isAdmin });

  if (loading) return null;
  if (!isAuthed) return <div className="lm-container py-12"><LoginPrompt title="Administration" /></div>;
  if (!isAdmin) return <div className="lm-container py-12"><EmptyState icon={ShieldCheck} title="Accès réservé aux administrateurs" testId="admin-forbidden" /></div>;

  const sync = async () => {
    try {
      const { data } = await api.post("/admin/sync", { full: true });
      toast.success(data.message);
      setTimeout(() => { status.refetch(); queryClient.invalidateQueries(); }, 1500);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  const s = status.data;

  return (
    <div className="lm-container pb-20">
      <PageHeader eyebrow="Administration" title="Pilotage de l'annuaire" icon={ShieldCheck}
        description="État de la connexion à Notion, synchronisation du cache et utilisateurs."
        actions={<Button className="rounded-full px-5" onClick={sync} disabled={s?.syncing} data-testid="admin-sync-button">
          <RefreshCw className={`h-4 w-4 ${s?.syncing ? "animate-spin" : ""}`} /> {s?.syncing ? "Synchronisation…" : "Synchroniser maintenant"}
        </Button>} />
      {status.isLoading ? <LoadingGrid count={3} /> : status.isError ? <ErrorState onRetry={status.refetch} /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="lm-card p-5" data-testid="admin-data-source">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Source des données</div>
              <div className="mt-2 flex items-center gap-2 text-lg font-extrabold"><Database className="h-5 w-5 text-primary" />{s.data_source === "mock" ? "Démonstration (données fictives)" : "Notion"}</div>
            </div>
            <div className="lm-card p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Synchronisation automatique</div>
              <div className="mt-2 text-lg font-extrabold">Toutes les {Math.round(s.sync_interval_seconds / 60)} min</div>
            </div>
            <div className="lm-card p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Écritures en attente / en échec</div>
              <div className="mt-2 text-lg font-extrabold">{s.pending_writes} / {s.write_errors}</div>
            </div>
          </div>
          <div className="lm-card mt-6 overflow-hidden">
            <Table data-testid="admin-collections-table">
              <TableHeader>
                <TableRow className="bg-stone-50/80 hover:bg-stone-50/80">
                  <TableHead>Base Notion</TableHead><TableHead>Variable</TableHead><TableHead className="text-right">Éléments</TableHead>
                  <TableHead>Dernière synchro</TableHead><TableHead>Vérification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.collections.map((c) => (
                  <TableRow key={c.key} data-testid={`admin-collection-${c.key}`}>
                    <TableCell className="font-semibold">{c.label}</TableCell>
                    <TableCell className="font-mono text-xs text-stone-500">{c.env}</TableCell>
                    <TableCell className="text-right font-semibold">{formatNumber(c.count)}</TableCell>
                    <TableCell className="text-sm text-stone-600">{dateTime(c.last_sync)}{c.error && <div className="text-xs font-semibold text-red-600">{c.error}</div>}</TableCell>
                    <TableCell className="max-w-sm"><MappingStatus mapping={c.mapping} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      <h2 className="mb-4 mt-10 text-xl font-extrabold">Contenus administrables</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        {Object.values(KINDS).map((k) => (
          <Link key={k.key} to={k.path} className="lm-card lm-card-hover flex items-center gap-3 p-5 font-semibold" data-testid={`admin-manage-${k.key}`}>
            <k.icon className="h-5 w-5 text-primary" /> Gérer les {k.label.toLowerCase()}
          </Link>
        ))}
      </div>
      <h2 className="mb-1 mt-10 flex items-center gap-2 text-xl font-extrabold"><Users className="h-5 w-5" /> Utilisateurs</h2>
      <div className="mb-4 space-y-1.5 text-sm text-muted-foreground" data-testid="admin-users-rules">
        <p>Seuls les membres de l'équipe de la base Notion <strong>team_ca</strong> sont listés (Statut « Team Mêlée »). Les Membres CA ne figurent pas dans cette liste.</p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Connexion</strong> : adresse en <strong>@lamelee.com</strong> + Statut « Team Mêlée » ou « Membre CA ». Les autres statuts (New, RIP…) et les adresses externes sont refusés.</li>
          <li><strong>Rôle Admin</strong> : « Type de profil » contient <strong>Responsable</strong> ou <strong>Data</strong>. Sinon, rôle User.</li>
          <li><strong>Gestion</strong> : toute modification (ajout, statut, profil) se fait directement dans Notion ; elle est prise en compte à la synchronisation suivante (≈ 2 min).</li>
        </ul>
      </div>
      {users.data && (
        <div className="lm-card overflow-hidden">
          <Table data-testid="admin-users-table">
            <TableHeader><TableRow className="bg-stone-50/80 hover:bg-stone-50/80"><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {users.data.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-stone-600">{u.email}</TableCell>
                  <TableCell><span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-primary">{u.role}</span></TableCell>
                  <TableCell className={u.active ? "text-sm text-green-700" : "text-sm text-stone-400"}>{u.active ? `Autorisé · ${u.status}` : `Non autorisé (email hors @lamelee.com) · ${u.status}`}</TableCell>
                  <TableCell>{u.notion_url && <a href={u.notion_url} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-primary" aria-label="Notion"><ExternalLink className="h-4 w-4" /></a>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
